<template>
  <div
    v-if="open"
    class="signup-backdrop login open"
    @mousedown="onBackdropMouseDown"
  >
    <div
      ref="modal"
      class="signup-modal login"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-title"
    >
      <div class="sheet-handle" aria-hidden="true" />
      <button
        type="button"
        class="signup-close"
        aria-label="Schließen"
        @click="close"
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        >
          <path d="M6 6 L18 18 M18 6 L6 18" />
        </svg>
      </button>
      <div class="signup-modal-body">
        <div class="login-mark">
          <VibelaMark :size="42" />
        </div>
        <div class="step-eyebrow login-eyebrow">
          Willkommen zurück
        </div>
        <h2 id="login-title" class="step-h2 login-headline">
          Schön, dich <em class="italic">wiederzusehen</em>.
        </h2>
        <p class="login-lead">
          Vibela lebt in der App — dort meldest du dich an. Hol sie dir hier, falls du sie noch nicht hast.
        </p>
        <div class="login-section-label">Lade die App</div>
        <div class="dl-badges login-badges">
          <a
            :href="appStoreUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="dl-badge"
            @click="onStoreClick('apple')"
          >
            <span class="badge-glyph">
              <svg width="22" height="24" viewBox="0 0 24 24" fill="#FFF4E6" aria-hidden="true">
                <path
                  d="M17.3 12.7c0-2.4 1.9-3.5 2-3.6-1.1-1.6-2.8-1.8-3.4-1.9-1.5-.1-2.8.8-3.5.8-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.1 1.7 2.4 3 2.4 1.2 0 1.6-.8 3.1-.8s1.9.8 3.1.8c1.3 0 2.1-1.1 2.9-2.3.9-1.3 1.3-2.6 1.3-2.7 0 0-2.5-.9-2.7-3.8zM15 5.4c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.7 1.4-.6.7-1.1 1.8-1 2.9 1 .1 2-.5 2.7-1.3z"
                />
              </svg>
            </span>
            <span class="badge-text">
              <span class="badge-small">Lade im</span>
              <span class="badge-big">App Store</span>
            </span>
          </a>
          <a
            :href="playStoreUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="dl-badge"
            @click="onStoreClick('google')"
          >
            <span class="badge-glyph">
              <svg width="22" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <defs>
                  <linearGradient :id="playBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#67D5FF" />
                    <stop offset="100%" stop-color="#1E88E5" />
                  </linearGradient>
                  <linearGradient :id="playGreen" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#00E676" />
                    <stop offset="100%" stop-color="#00B884" />
                  </linearGradient>
                  <linearGradient :id="playYellow" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#FFE94B" />
                    <stop offset="100%" stop-color="#FFB300" />
                  </linearGradient>
                  <linearGradient :id="playRed" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#FF6B6B" />
                    <stop offset="100%" stop-color="#E63946" />
                  </linearGradient>
                </defs>
                <path d="M4 2.5 L13 12 L4 21.5 Z" :fill="`url(#${playBlue})`" />
                <path d="M4 2.5 L17 10 L13 12 Z" :fill="`url(#${playGreen})`" />
                <path d="M17 10 L21 12 L17 14 Z" :fill="`url(#${playYellow})`" />
                <path d="M4 21.5 L17 14 L13 12 Z" :fill="`url(#${playRed})`" />
              </svg>
            </span>
            <span class="badge-text">
              <span class="badge-small">Jetzt bei</span>
              <span class="badge-big">Google Play</span>
            </span>
          </a>
        </div>
        <div v-if="showQr" class="dl-qr-block">
          <div class="dl-qr-vis">
            <StyledQRCode :data="smartLink" :size="92" />
          </div>
          <div class="dl-qr-text">
            <div class="qr-title">Scan mit deinem Handy</div>
            <div class="qr-sub">
              Öffnet Vibela auf deinem Telefon — falls noch nicht installiert, führt der Smart-Link automatisch zum Store.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VibelaMark from './VibelaMark.vue';
import StyledQRCode from './StyledQRCode.vue';
import {
  buildStoreUrls,
  isQrViewport,
  resolveCtaMeta,
  trackEvent,
} from '../analytics/ga4';

const DEFAULT_CTA = {
  cta_location: 'header_login',
  cta_intent: 'login',
};

export default {
  name: 'LoginModal',
  components: { VibelaMark, StyledQRCode },
  data() {
    const urls = buildStoreUrls(DEFAULT_CTA.cta_location, DEFAULT_CTA.cta_intent);
    return {
      open: false,
      platform: 'desktop',
      showQr: false,
      triggerEl: null,
      bodyOverflow: '',
      openedAt: 0,
      ctaLocation: DEFAULT_CTA.cta_location,
      ctaIntent: DEFAULT_CTA.cta_intent,
      smartLink: urls.smartLink,
      appStoreUrl: urls.appStoreUrl,
      playStoreUrl: urls.playStoreUrl,
      playBlue: `login-play-blue-${this._uid}`,
      playGreen: `login-play-green-${this._uid}`,
      playYellow: `login-play-yellow-${this._uid}`,
      playRed: `login-play-red-${this._uid}`,
    };
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.lockScroll();
        this.$nextTick(() => {
          this.scrollBackdropTop();
          document.addEventListener('keydown', this.onKeyDown);
        });
      } else {
        this.unlockScroll();
        document.removeEventListener('keydown', this.onKeyDown);
        setTimeout(() => {
          if (this.triggerEl && typeof this.triggerEl.focus === 'function') {
            this.triggerEl.focus();
          }
        }, 200);
      }
    },
  },
  created() {
    this.detectPlatform();
    window.__openLogin = (trigger) => this.openModal(trigger);
    if (new URLSearchParams(window.location.search).get('login') === 'true') {
      this.openModal(null, DEFAULT_CTA);
    }
    document.addEventListener('click', this.onLoginLinkClick, true);
  },
  beforeDestroy() {
    delete window.__openLogin;
    document.removeEventListener('click', this.onLoginLinkClick, true);
    document.removeEventListener('keydown', this.onKeyDown);
    this.unlockScroll();
  },
  methods: {
    detectPlatform() {
      const ua = navigator.userAgent.toLowerCase();
      if (/iphone|ipad|ipod/.test(ua)) {
        this.platform = 'ios';
      } else if (/android/.test(ua)) {
        this.platform = 'android';
      } else {
        this.platform = 'desktop';
      }
      this.showQr = isQrViewport();
    },
    applyCtaMeta(meta) {
      this.ctaLocation = meta.cta_location;
      this.ctaIntent = meta.cta_intent;
      const urls = buildStoreUrls(meta.cta_location, meta.cta_intent);
      this.appStoreUrl = urls.appStoreUrl;
      this.playStoreUrl = urls.playStoreUrl;
      this.smartLink = urls.smartLink;
    },
    openModal(trigger, defaults = DEFAULT_CTA) {
      const meta = resolveCtaMeta(trigger, defaults);
      this.applyCtaMeta(meta);
      this.triggerEl = trigger || null;
      this.showQr = isQrViewport();
      this.openedAt = Date.now();
      this.open = true;
      trackEvent('app_modal_open', {
        cta_location: meta.cta_location,
        cta_intent: meta.cta_intent,
      });
      if (this.showQr) {
        trackEvent('app_qr_view', { cta_location: meta.cta_location });
      }
    },
    close() {
      if (!this.open) {
        return;
      }
      const timeOpenSec = this.openedAt
        ? Math.max(0, Math.round((Date.now() - this.openedAt) / 1000))
        : 0;
      trackEvent('app_modal_close', {
        cta_location: this.ctaLocation,
        time_open_sec: timeOpenSec,
      });
      this.open = false;
      this.stripLoginQuery();
    },
    onStoreClick(store) {
      trackEvent('app_store_click', {
        store,
        cta_location: this.ctaLocation,
        cta_intent: this.ctaIntent,
      });
    },
    onBackdropMouseDown(event) {
      if (event.target === event.currentTarget) {
        this.close();
      }
    },
    onLoginLinkClick(event) {
      const link = event.target.closest('a[href*="login=true"]');
      if (!link) {
        return;
      }
      event.preventDefault();
      this.openModal(link, DEFAULT_CTA);
    },
    stripLoginQuery() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('login') !== 'true') {
        return;
      }
      url.searchParams.delete('login');
      const search = url.searchParams.toString();
      const next = url.pathname + (search ? `?${search}` : '') + url.hash;
      history.replaceState(null, '', next);
    },
    lockScroll() {
      this.bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    },
    unlockScroll() {
      document.body.style.overflow = this.bodyOverflow;
    },
    scrollBackdropTop() {
      requestAnimationFrame(() => {
        const backdrop = document.querySelector('.signup-backdrop.login');
        if (backdrop) {
          backdrop.scrollTop = 0;
        }
      });
    },
    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.$refs.modal) {
        return;
      }
      const focusable = Array.from(
        this.$refs.modal.querySelectorAll(
          'a, button, input, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.login-mark {
  text-align: center;
  margin: 12px 0 18px;
}

.login-eyebrow {
  display: flex;
  justify-content: center;
}

.login-headline {
  text-align: center;
}

.login-lead {
  text-align: center;
  font-family: Fraunces, Georgia, serif;
  font-size: 16px;
  color: var(--mute);
  line-height: 1.5;
  max-width: 380px;
  margin: 14px auto 26px;
}

.login-section-label {
  text-align: center;
  font-family: Fraunces, Georgia, serif;
  font-weight: 500;
  font-size: 11px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--mute);
  margin: 8px 0 14px;
}

.login-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}
.login-badges .dl-badge {
  flex: 1 1 auto;
}
</style>
